/*
  BlogPage.js
  -----------
  Purpose: This page provides an interface for creating, editing, and managing individual blog posts within the admin system. It allows administrators to input blog content, manage metadata, upload images, and configure FAQ sections.

  Main Logic:
  - Provides a rich text editor for blog content creation and editing.
  - Handles metadata input for SEO and publishing controls.
  - Supports image upload and preview for blog media.
  - Integrates with API endpoints for creating and updating blog posts.
  - Manages FAQ sections and additional blog configuration.
  - Utilizes React hooks for state management and side effects.
  - Displays success and failure modals for user feedback.
*/

/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import React, { useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
// import TextEditor from "../components/TextEditor"; // Commented out - Now using CKEditor 5
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Mention,
  Paragraph,
  Undo,
  Heading,
  Link,
  List,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageUpload,
  Underline,
  Strikethrough,
  BlockQuote,
  Alignment,
  SourceEditing,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  Font,
  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,
  Indent,
  IndentBlock,
  Code,
  CodeBlock,
  RemoveFormat,
  HorizontalLine,
  SpecialCharacters,
  SpecialCharactersEssentials,
  Subscript,
  Superscript,
  TodoList,
  MediaEmbed,
  PageBreak,
  FindAndReplace,
  SelectAll,
  ShowBlocks,
  PasteFromOffice,
  Clipboard,
  ClipboardPipeline,
  GeneralHtmlSupport,
  ShiftEnter,
  ButtonView,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import useCommonApi from "../hooks/common-api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SuccessModal from "../components/SuccessModal";
import FailureModal from "../components/FailureModal";
import Loader from "../components/Loader";
import { Accordion, Card, Col, Row } from "react-bootstrap";
import FaqComponent from "../components/FaqComponent";
import SimilarBlogs from "../components/SimilarBlogs";

function BlogPage() {
  const [editorData, setEditorData] = useState("<p></p>");
  const [showHtmlModal, setShowHtmlModal] = useState(false);
  const [rawHtmlInput, setRawHtmlInput] = useState("");
  // const [isSourceView, setIsSourceView] = useState(false); // Not needed with CKEditor source editing plugin
  const contentEditableRef = useRef(null);
  const editorRef = useRef(null); // Reference for CKEditor instance
  const imageRef = useRef(null);
  const faqComponentRef = useRef(null);
  const [rawFile, setrawFile] = useState(null);
  const [previewUrl, setpreviewUrl] = useState(null);
  const [inputData, setinputData] = useState({
    blog_title: "",
    blog_slug: "",
    blog_meta_title: "",
    blog_meta_description: "",
    publish_date: "",
  });
  const [loading, setloading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [openSuccessModal, setopenSuccessModal] = useState(undefined);
  const [openFailureModal, setopenFailureModal] = useState(undefined);
  const [successFailureMessage, setsuccessFailureMessage] = useState("");
  const removeModal = () => {
    setopenSuccessModal(undefined);
    setopenFailureModal(undefined);
  };
  const { state } = useLocation();
  const { createBlog, updateBlog, getBlogBySlug, getBlogById } = useCommonApi();
  const navigate = useNavigate();
  const params = useParams();
  const uploadedImages = useRef([]);
  const previousEditorData = useRef("<p></p>"); // Track previous editor content
  const [typingTimer, setTypingTimer] = useState(null);
  const typingTimeout = 1000; // Set the timeout value in milliseconds (adjust as needed)
  const [faqs, setFaqs] = useState([]);
  const [similarBlogs, setSimilarBlogs] = useState([]);
  const [isSlugAvailable, setIsSlugAvailable] = useState(true);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugChecked, setSlugChecked] = useState(false);

  // Function to generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters except spaces and hyphens
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
      .trim()
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  };

  // Function to check slug availability
  const checkSlugAvailability = async () => {
    if (!inputData.blog_slug) return;

    setCheckingSlug(true);
    setSlugChecked(false);
    try {
      const blogExists = await getBlogBySlug(inputData.blog_slug);

      // Check if it's the same blog we're editing (for update case)
      const isSameBlog = params?.id !== "new" && blogExists?.id === params?.id;

      setIsSlugAvailable(!blogExists || isSameBlog);
      setSlugChecked(true);
    } catch (error) {
      console.log("Error checking slug availability:", error);
      // On error, assume slug is available to not block the user
      setIsSlugAvailable(true);
      setSlugChecked(true);
    } finally {
      setCheckingSlug(false);
    }
  };

  const addFaq = (index) => {
    const newFaq = { question: "", content: "" };
    const updatedFaqs = [
      ...faqs.slice(0, index + 1),
      newFaq,
      ...faqs.slice(index + 1),
    ];
    setFaqs(updatedFaqs);
  };

  const addSimilarBlog = (index) => {
    const newSimilarBlog = { blog_title: "", id: "" };
    const updatedSimilarBlogs = [
      ...similarBlogs.slice(0, index + 1),
      newSimilarBlog,
      ...similarBlogs.slice(index + 1),
    ];
    setSimilarBlogs(updatedSimilarBlogs);
  };

  // Old handleImageDelete - commented out, now using new implementation below
  /*
  const handleImageDelete = (oldData, newData) => {
    const oldImages = Array.from(oldData.matchAll(/<img.*?src="(.*?)"/g)).map(
      (match) => match[1]
    );
    const newImages = Array.from(newData.matchAll(/<img.*?src="(.*?)"/g)).map(
      (match) => match[1]
    );
    console.log(oldImages, newImages, "images");

    uploadedImages?.current?.forEach((oldImage) => {
      if (!newImages.includes(oldImage)) {
        deleteImage(oldImage, "editor");
      }
    });
  };
  */

  const uploadIconFn = async (file) => {
    try {
      let formdata = new FormData();

      formdata.append("vsn_file", file);

      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/upload/files/editorimage`,
        method: "POST",
        headers: {
          "Content-Type": "multipart/form-data",
          jwt_token: localStorage.getItem("admin_user_token"),
        },
        timeout: 120000,
        data: formdata,
      });

      if (!response.data.error) {
        let blog_image = response.data.file_path;
        let admin_selected_blog = JSON.parse(
          localStorage.getItem("admin_selected_blog")
        );
        admin_selected_blog.blog_image = blog_image;
        localStorage.setItem(
          "admin_selected_blog",
          JSON.stringify(admin_selected_blog)
        );
        setinputData({
          ...inputData,
          blog_image: blog_image,
        });
        createBlogFn(null, editorData);
        return {
          error: false,
          data: response.data.file_path,
        };
      } else {
        return {
          error: true,
          data: response.data.message,
        };
      }
    } catch (error) {
      console.log(error);
      return {
        error: true,
        data: error.message,
      };
    }
  };

  const createBlogFn = async (e, newData = "", from = "editor") => {
    if (e) {
      e.preventDefault();
    }

    // Sync all FAQ contents before saving
    if (faqComponentRef.current && faqComponentRef.current.syncAllFaqContents) {
      faqComponentRef.current.syncAllFaqContents();
      // Wait a tiny bit for state to update
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // If rawfile
    let blog_image =
      JSON.parse(localStorage.getItem("admin_selected_blog")).blog_image || "";

    // if (JSON.parse(localStorage.getItem("admin_selected_blog")).blog_image) {
    // blog_image = JSON.parse(
    //   localStorage.getItem("admin_selected_blog")
    // ).blog_image;
    // } else {
    //   if (rawFile) {
    //     let response = await uploadIconFn();
    //     blog_image = response.data;
    //   } else {
    //     blog_image = "";
    //   }
    // }
    // console.log(blog_image, "blog image");
    // let login data

    // Get content from CKEditor instead of contentEditableRef
    let cleanedHTML = editorData;

    // Remove 'table' class from figure elements to prevent Bootstrap conflicts
    // and add inline styles for horizontal scrolling on wide tables
    let tempElement = document.createElement("div");
    tempElement.innerHTML = cleanedHTML;
    let tableFigures = tempElement.querySelectorAll("figure.table");
    tableFigures.forEach((figure) => {
      figure.classList.remove("table");
      figure.style.overflowX = "auto";
      figure.style.maxWidth = "100%";
    });
    cleanedHTML = tempElement.innerHTML;

    // Old approach with contentEditableRef - commented out
    /*
    let tempElement = document.createElement("div");
    tempElement.innerHTML = contentEditableRef.current.innerHTML;

    // Get all elements with class 'hover-buttons' and remove them
    let hoverButtons = tempElement.querySelectorAll(".hover-buttons");
    hoverButtons.forEach((button) => button.parentNode.removeChild(button));

    // Get the cleaned HTML
    let cleanedHTML = tempElement.innerHTML;
    */

    console.log(cleanedHTML, "cleaned");
    let loginData = JSON.parse(localStorage.getItem("admin_user_data"));
    let data = {
      blog_cat_id: "0",
      blog_title: inputData.blog_title,
      blog_slug: inputData.blog_slug,
      blog_image: blog_image,
      blog_meta_title: inputData.blog_meta_title,
      blog_meta_description: inputData.blog_meta_description,
      publish_date: inputData.publish_date,
      blog_content: cleanedHTML,
      featured: "0",
      status: "1",
      author_name: loginData?.first_name + " " + loginData?.last_name,
      author_image: loginData?.profile_photo,
      faqs: faqs,
      blog_related: similarBlogs.map((blog) => blog.id).filter((id) => id),
    };
    if (params?.id == "new") {
      setloading(true);
      let res = await createBlog(data);
      setloading(false);
      if (!res.error) {
        setsuccessFailureMessage(res.message);
        setopenSuccessModal("dismissible");
        navigate(`/blog/create/${res?.data?.id}`, { state });
      } else {
        setsuccessFailureMessage(res.message);
        setopenSuccessModal("dismissible");
      }

      localStorage.setItem(
        "admin_selected_blog",
        JSON.stringify({
          id: res?.data?.id,
          ...data,
        })
      );
    } else {
      let updatedData = {
        id: params?.id,
        ...data,
        featured: JSON.parse(localStorage.getItem("admin_selected_blog"))
          ?.featured,
        status: JSON.parse(localStorage.getItem("admin_selected_blog"))?.status,
        faqs: faqs,
        blog_related: similarBlogs.map((blog) => blog.id).filter((id) => id),
      };
      setloading(true);
      let res = await updateBlog(updatedData);
      setloading(false);
      if (!res.error) {
        console.log("came here...");
        setsuccessFailureMessage(res.message);
        setopenSuccessModal("dismissible");
      } else {
        setsuccessFailureMessage(res.message);
        setopenSuccessModal("dismissible");
      }
      localStorage.setItem(
        "admin_selected_blog",
        JSON.stringify({
          ...updatedData,
        })
      );
    }
  };
  const uploadImage = async (file) => {
    console.log(file, "file");
    let formdata = new FormData();
    formdata.append("vsn_file", file);

    const response = await axios({
      url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/upload/files/editorimage`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
        jwt_token: localStorage.getItem("admin_user_token"),
      },
      timeout: 120000,
      data: formdata,
    });

    if (!response.data.error) {
      return {
        default: response.data.file_path,
      };
    } else {
      throw new Error(response.data.message);
    }
  };

  // Custom Upload Adapter for CKEditor
  class CustomUploadAdapter {
    constructor(loader) {
      this.loader = loader;
    }

    upload() {
      return this.loader.file.then(
        (file) =>
          new Promise((resolve, reject) => {
            uploadImage(file)
              .then((response) => {
                resolve({
                  default: response.default,
                });
              })
              .catch((error) => {
                reject(error);
              });
          })
      );
    }

    abort() {
      // Handle abort
    }
  }

  // Custom Upload Adapter Plugin
  function CustomUploadAdapterPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      return new CustomUploadAdapter(loader);
    };
  }

  // Custom Insert HTML toolbar button plugin
  // Defined inside component so it closes over setShowHtmlModal
  function InsertHtmlPlugin(editor) {
    editor.ui.componentFactory.add("insertHtml", (locale) => {
      const button = new ButtonView(locale);
      button.set({
        label: "Insert HTML",
        // HTML/code brackets icon (20x20 CKEditor-style SVG)
        icon: '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M6.91 3.96 2.05 10l4.86 6.04.92-.74L3.06 10l4.77-5.3-.92-.74zm6.18 0-.92.74L16.94 10l-4.77 5.3.92.74L18.95 10l-4.86-6.04z"/></svg>',
        tooltip: "Insert HTML Block",
        withText: false,
      });
      button.on("execute", () => {
        setShowHtmlModal(true);
      });
      return button;
    });
  }

  // Function to detect and delete removed images from editor
  const handleImageDelete = (newData) => {
    const oldImages = Array.from(
      previousEditorData.current.matchAll(/<img.*?src="(.*?)"/g)
    ).map((match) => match[1]);
    const newImages = Array.from(newData.matchAll(/<img.*?src="(.*?)"/g)).map(
      (match) => match[1]
    );

    console.log("Old images:", oldImages);
    console.log("New images:", newImages);

    // Find images that were removed
    oldImages.forEach((oldImage) => {
      if (!newImages.includes(oldImage)) {
        console.log("Deleting removed image:", oldImage);
        deleteImageFromServer(oldImage);
      }
    });
  };

  // Function to delete image from server
  const deleteImageFromServer = async (filePath) => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/delete/upload_file`,
        method: "POST",
        headers: {
          jwt_token: localStorage.getItem("admin_user_token"),
        },
        timeout: 120000,
        data: {
          file_path: filePath,
        },
      });

      if (!response.data.error) {
        console.log("Image deleted successfully:", filePath);
      } else {
        console.log("Error deleting image:", response.data.message);
      }
    } catch (error) {
      console.log("Error deleting image:", error);
    }
  };

  const deleteImage = async (filePath, from = "editor") => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/delete/upload_file`,
        method: "POST",
        headers: {
          jwt_token: localStorage.getItem("admin_user_token"),
        },
        timeout: 120000,
        data: {
          file_path: filePath,
        },
      });
      setrawFile(null);
      setpreviewUrl(null);
      imageRef.current.value = "";
      let admin_selected_blog = JSON.parse(
        localStorage.getItem("admin_selected_blog")
      );
      admin_selected_blog.blog_image = "";
      localStorage.setItem(
        "admin_selected_blog",
        JSON.stringify(admin_selected_blog)
      );
      setinputData({
        ...inputData,
        blog_image: "",
      });
      createBlogFn(null, editorData);
      if (!response.data.error) {
        // Handle success
      } else {
        // Handle error
      }
    } catch (error) {
      console.log(error);
    }
  };

  // CKEditor Configuration
  const editorConfiguration = {
    licenseKey: "GPL", // or 'GPL' for open source usage
    plugins: [
      Bold,
      Essentials,
      Italic,
      Paragraph,
      Undo,
      Heading,
      Link,
      List,
      TodoList,
      Image,
      ImageToolbar,
      ImageCaption,
      ImageStyle,
      ImageResize,
      ImageUpload,
      Underline,
      Strikethrough,
      BlockQuote,
      Alignment,
      SourceEditing,
      Table,
      TableToolbar,
      TableProperties,
      TableCellProperties,
      FontFamily,
      FontSize,
      FontColor,
      FontBackgroundColor,
      Indent,
      IndentBlock,
      Code,
      CodeBlock,
      RemoveFormat,
      HorizontalLine,
      SpecialCharacters,
      SpecialCharactersEssentials,
      Subscript,
      Superscript,
      MediaEmbed,
      PageBreak,
      FindAndReplace,
      SelectAll,
      ShowBlocks,
      PasteFromOffice,
      Clipboard,
      ClipboardPipeline,
      GeneralHtmlSupport,
      ShiftEnter,
      CustomUploadAdapterPlugin,
      InsertHtmlPlugin,
    ],
    toolbar: {
      items: [
        "undo",
        "redo",
        "|",
        "findAndReplace",
        "selectAll",
        "|",
        "heading",
        "|",
        "fontSize",
        "fontFamily",
        "fontColor",
        "fontBackgroundColor",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "subscript",
        "superscript",
        "code",
        "|",
        "removeFormat",
        "|",
        "link",
        "uploadImage",
        "mediaEmbed",
        "insertTable",
        "blockQuote",
        "codeBlock",
        "horizontalLine",
        "pageBreak",
        "|",
        "alignment",
        "|",
        "bulletedList",
        "numberedList",
        "todoList",
        "|",
        "outdent",
        "indent",
        "|",
        "specialCharacters",
        "|",
        "sourceEditing",
        "showBlocks",
        "|",
        "insertHtml",
      ],
      shouldNotGroupWhenFull: true,
    },
    fontSize: {
      options: [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48],
      supportAllValues: true,
    },
    fontFamily: {
      options: [
        "default",
        "Arial, Helvetica, sans-serif",
        "Courier New, Courier, monospace",
        "Georgia, serif",
        "Lucida Sans Unicode, Lucida Grande, sans-serif",
        "Tahoma, Geneva, sans-serif",
        "Times New Roman, Times, serif",
        "Trebuchet MS, Helvetica, sans-serif",
        "Verdana, Geneva, sans-serif",
        "Roboto, sans-serif",
        "Open Sans, sans-serif",
        "Lato, sans-serif",
        "Montserrat, sans-serif",
      ],
      supportAllValues: true,
    },
    fontColor: {
      colors: [
        { color: "hsl(0, 0%, 0%)", label: "Black" },
        { color: "hsl(0, 0%, 30%)", label: "Dim grey" },
        { color: "hsl(0, 0%, 60%)", label: "Grey" },
        { color: "hsl(0, 0%, 90%)", label: "Light grey" },
        { color: "hsl(0, 0%, 100%)", label: "White", hasBorder: true },
        { color: "hsl(0, 75%, 60%)", label: "Red" },
        { color: "hsl(30, 75%, 60%)", label: "Orange" },
        { color: "hsl(60, 75%, 60%)", label: "Yellow" },
        { color: "hsl(90, 75%, 60%)", label: "Light green" },
        { color: "hsl(120, 75%, 60%)", label: "Green" },
        { color: "hsl(150, 75%, 60%)", label: "Aquamarine" },
        { color: "hsl(180, 75%, 60%)", label: "Turquoise" },
        { color: "hsl(210, 75%, 60%)", label: "Light blue" },
        { color: "hsl(240, 75%, 60%)", label: "Blue" },
        { color: "hsl(270, 75%, 60%)", label: "Purple" },
      ],
    },
    fontBackgroundColor: {
      colors: [
        { color: "hsl(0, 0%, 0%)", label: "Black" },
        { color: "hsl(0, 0%, 30%)", label: "Dim grey" },
        { color: "hsl(0, 0%, 60%)", label: "Grey" },
        { color: "hsl(0, 0%, 90%)", label: "Light grey" },
        { color: "hsl(0, 0%, 100%)", label: "White", hasBorder: true },
        { color: "hsl(0, 75%, 60%)", label: "Red" },
        { color: "hsl(30, 75%, 60%)", label: "Orange" },
        { color: "hsl(60, 75%, 60%)", label: "Yellow" },
        { color: "hsl(90, 75%, 60%)", label: "Light green" },
        { color: "hsl(120, 75%, 60%)", label: "Green" },
        { color: "hsl(150, 75%, 60%)", label: "Aquamarine" },
        { color: "hsl(180, 75%, 60%)", label: "Turquoise" },
        { color: "hsl(210, 75%, 60%)", label: "Light blue" },
        { color: "hsl(240, 75%, 60%)", label: "Blue" },
        { color: "hsl(270, 75%, 60%)", label: "Purple" },
      ],
    },
    heading: {
      options: [
        {
          model: "paragraph",
          title: "Paragraph",
          class: "ck-heading_paragraph",
        },
        {
          model: "heading1",
          view: "h1",
          title: "Heading 1",
          class: "ck-heading_heading1",
        },
        {
          model: "heading2",
          view: "h2",
          title: "Heading 2",
          class: "ck-heading_heading2",
        },
        {
          model: "heading3",
          view: "h3",
          title: "Heading 3",
          class: "ck-heading_heading3",
        },
        {
          model: "heading4",
          view: "h4",
          title: "Heading 4",
          class: "ck-heading_heading4",
        },
        {
          model: "heading5",
          view: "h5",
          title: "Heading 5",
          class: "ck-heading_heading5",
        },
        {
          model: "heading6",
          view: "h6",
          title: "Heading 6",
          class: "ck-heading_heading6",
        },
      ],
    },
    image: {
      toolbar: [
        "imageStyle:inline",
        "imageStyle:block",
        "imageStyle:side",
        "|",
        "toggleImageCaption",
        "imageTextAlternative",
        "|",
        "resizeImage",
      ],
      resizeOptions: [
        {
          name: "resizeImage:original",
          label: "Original",
          value: null,
        },
        {
          name: "resizeImage:25",
          label: "25%",
          value: "25",
        },
        {
          name: "resizeImage:50",
          label: "50%",
          value: "50",
        },
        {
          name: "resizeImage:75",
          label: "75%",
          value: "75",
        },
      ],
    },
    alignment: {
      options: ["left", "center", "right", "justify"],
    },
    table: {
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableProperties",
        "tableCellProperties",
      ],
    },
    link: {
      defaultProtocol: "https://",
      decorators: {
        openInNewTab: {
          mode: "manual",
          label: "Open in a new tab",
          attributes: {
            target: "_blank",
            rel: "noopener noreferrer",
          },
        },
      },
    },
    // Preserve all HTML elements and styles when pasting (especially from Google Docs)
    htmlSupport: {
      allow: [
        {
          name: /.*/,
          attributes: true,
          classes: true,
          styles: true,
        },
      ],
      disallow: [],
    },
  };

  // Commented out old editor configuration - keeping for reference
  /*
  class MyUploadAdapter {
    constructor(loader) {
      this.loader = loader;
    }

    // Starts the upload process.
    async upload() {
      return this.loader.file.then(
        (file) =>
          new Promise((resolve, reject) => {
            this._uploadImage(file)
              .then((response) => {
                resolve({
                  default: response.default,
                });
              })
              .catch((error) => {
                reject(error);
              });
          })
      );
    }

    // Handles the actual upload process.
    async _uploadImage(file) {
      return uploadImage(file);
    }

    abort() {
      // Reject the promise returned from the upload() method.
    }
  }

  function MyCustomUploadAdapterPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      return new MyUploadAdapter(loader);
    };
  }
  */
  // Fetch blog data when editing existing blog (using id)
  const fetchBlogData = async () => {
    if (params?.id !== "new" && params?.id) {
      setInitialLoading(true);
      try {
        const blogData = await getBlogById(params.id);
        if (blogData) {
          const formattedDate = blogData.publish_date
            ? new Date(blogData.publish_date).toISOString().split("T")[0]
            : "";

          setinputData({
            blog_title: blogData.blog_title || "",
            blog_slug: blogData.blog_slug || "",
            blog_meta_title: blogData.blog_meta_title || "",
            blog_meta_description: blogData.blog_meta_description || "",
            publish_date: formattedDate,
            blog_image: blogData.blog_image || "",
          });

          setFaqs(blogData.faqs || []);
          setSimilarBlogs(blogData?.related_blogs || []);
          setpreviewUrl(blogData.blog_image);
          setEditorData(blogData.blog_content || "<p></p>");

          // Initialize previousEditorData with the fetched content
          previousEditorData.current = blogData.blog_content || "<p></p>";

          // Store in localStorage for other functions that might need it
          localStorage.setItem("admin_selected_blog", JSON.stringify(blogData));
        }
      } catch (error) {
        console.log("Error fetching blog data:", error);
      } finally {
        setInitialLoading(false);
      }
    } else {
      // Reset for new blog
      setinputData({
        blog_title: "",
        blog_slug: "",
        blog_meta_title: "",
        blog_meta_description: "",
        publish_date: "",
      });
      setFaqs([]);
      setSimilarBlogs([]);
      setpreviewUrl(null);
      setEditorData("<p></p>");
      previousEditorData.current = "<p></p>"; // Initialize for new blog
      localStorage.setItem("admin_selected_blog", JSON.stringify({}));
    }
  };

  useEffect(() => {
    fetchBlogData();
  }, [params?.id]);

  useEffect(() => {
    if (!rawFile) {
      return;
    }
    const objectURL = URL.createObjectURL(rawFile);
    setpreviewUrl(objectURL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawFile]);

  // Commented out old editor configuration - now using CKEditor configuration defined above
  /*
  const editorConfiguration = {
    extraPlugins: [MyCustomUploadAdapterPlugin],
    alignment: {
      options: ["left", "right"],
    },
    toolbar: [
      "heading",
      "|",
      "bold",
      "italic",
      "underline",
      "link",
      "bulletedList",
      "numberedList",
      "blockQuote",
      "imageUpload",
      "undo",
      "redo",
      "|",
      "alignment:left",
      "alignment:center",
      "alignment:right",
      "alignment:justify",
    ],
  };
  */

  // Insert raw HTML at the current cursor position in CKEditor
  const insertRawHtml = () => {
    const editor = editorRef.current;
    if (!editor || !rawHtmlInput.trim()) return;

    try {
      const viewFragment = editor.data.processor.toView(rawHtmlInput.trim());
      const modelFragment = editor.data.toModel(viewFragment);
      editor.model.insertContent(modelFragment);
    } catch (err) {
      console.error("Error inserting HTML:", err);
    }

    setRawHtmlInput("");
    setShowHtmlModal(false);
  };

  const handleContentChange = (newData) => {
    // Temporarily disabled auto-save
    /*
    if (typingTimer) {
      clearTimeout(typingTimer);
    }

    // Set a new timer to call the save API after typingTimeout milliseconds
    const newTypingTimer = setTimeout(() => {
      createBlogFn(null, newData);
    }, typingTimeout);

    setTypingTimer(newTypingTimer);
    */
  };

  // console.log(inputData, "input data");
  console.log(faqs, "faqs");

  // console.log(previewUrl, "preview url");

  // Show centered loader while fetching blog data
  if (initialLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <Form
        onSubmit={(e) => {
          createBlogFn(e, editorData);
        }}
      >
        <Form.Group className="mb-3 mt-3">
          <Form.Label>Blog Title</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter Blog Title"
            value={inputData.blog_title}
            onChange={(e) => {
              const newTitle = e.target.value;
              const newSlug = generateSlug(newTitle);
              setinputData({
                ...inputData,
                blog_title: newTitle,
                blog_slug: newSlug,
              });
              // Reset slug availability when title changes
              setIsSlugAvailable(true);
              setSlugChecked(false);
            }}
            required
          />
        </Form.Group>
        <Form.Group className="mb-3 mt-3">
          <Form.Label>Blog Slug</Form.Label>
          <div className="d-flex align-items-center gap-2">
            <Form.Control
              type="text"
              placeholder="Enter Blog Slug"
              value={inputData.blog_slug}
              onChange={(e) => {
                setinputData({
                  ...inputData,
                  blog_slug: e.target.value,
                });
                // Reset availability when slug is manually changed
                setIsSlugAvailable(true);
                setSlugChecked(false);
              }}
              required
              className={!isSlugAvailable ? "is-invalid" : ""}
            />
            <Button
              type="button"
              variant="outline-primary"
              onClick={checkSlugAvailability}
              disabled={!inputData.blog_slug || checkingSlug}
              className="flex-shrink-0"
            >
              {checkingSlug ? "Checking..." : "Check Availability"}
            </Button>
          </div>
          {!isSlugAvailable && (
            <div className="invalid-feedback d-block">
              This slug is already taken. Please choose a different one.
            </div>
          )}
          {isSlugAvailable &&
            inputData.blog_slug &&
            slugChecked &&
            !checkingSlug && (
              <div className="valid-feedback d-block">
                Great! This slug is available.
              </div>
            )}
          <Form.Text className="text-muted">
            URL-friendly version of the title. Only lowercase letters, numbers,
            and hyphens allowed.
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-3 mt-3">
          <Form.Label>Blog Meta Title</Form.Label>
          <Form.Control
            type="text"
            placeholder="Blog Meta Title"
            value={inputData.blog_meta_title}
            onChange={(e) => {
              setinputData({
                ...inputData,
                blog_meta_title: e.target.value,
              });
            }}
            // maxLength="60"
            required
          />
        </Form.Group>
        <Form.Group className="mb-3 mt-3">
          <Form.Label>Blog Meta Description</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter Blog Meta Description"
            value={inputData.blog_meta_description}
            onChange={(e) => {
              setinputData({
                ...inputData,
                blog_meta_description: e.target.value,
              });
            }}
            // maxLength="160"
            required
          />
        </Form.Group>
        <Form.Group className="mb-3 mt-3">
          <Form.Label>Blog Publish Date</Form.Label>
          <Form.Control
            type="date"
            placeholder="Enter Blog Publish Date"
            value={inputData.publish_date}
            onChange={(e) => {
              setinputData({
                ...inputData,
                publish_date: e.target.value,
              });
            }}
            required
          />
        </Form.Group>
        <Form.Group controlId="formFile" className="mb-3">
          <Form.Label>Blog Image</Form.Label>
          <Form.Control
            type="file"
            ref={imageRef}
            accept="image/*"
            onChange={(e) => {
              uploadIconFn(e.target.files[0]);
            }}
          />

          <div className="mt-3 mb-3">
            {inputData.blog_image ? (
              <div className="w-[150px] h-[150px] rounded-[50%] flex items-center justify-center relative">
                <img
                  src={inputData.blog_image}
                  alt="blog"
                  className="w-full object-cover"
                />
                <button
                  type="button"
                  className="absolute right-0 top-0 text-[#DC3545] hover:text-[#B52F3C]"
                  onClick={() => {
                    deleteImage(inputData.blog_image, "blog");
                  }}
                >
                  <svg className="icon text-[26px]">
                    <use href="#icon_delete"></use>
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <div></div>
              </>
            )}
          </div>
        </Form.Group>

        <Button
          type="submit"
          disabled={!isSlugAvailable}
          className="logout-btn grd-btn py-2"
        >
          {loading ? (
            <>
              <Loader />
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Form>
      <FaqComponent
        ref={faqComponentRef}
        faqs={faqs}
        setFaqs={setFaqs}
        addFaq={addFaq}
      />
      <Button
        variant="primary"
        onClick={() => addFaq(faqs.length - 1)}
        className="mt-2 mb-3 grd-btn"
      >
        Add FAQ
      </Button>

      {/* similar blogs section */}
      <SimilarBlogs
        similarBlogs={similarBlogs}
        setSimilarBlogs={setSimilarBlogs}
        currentBlog={inputData}
      />
      <Button
        variant="primary"
        onClick={() => addSimilarBlog(similarBlogs.length - 1)}
        className="mt-2 mb-3 grd-btn"
      >
        Add Similar Blog
      </Button>

      {/* Insert HTML Modal — triggered by CKEditor toolbar button */}
      {showHtmlModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "hsla(0,0%,0%,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            fontFamily:
              '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowHtmlModal(false);
              setRawHtmlInput("");
            }
          }}
        >
          <div
            style={{
              background: "hsl(0,0%,100%)",
              borderRadius: "2px",
              width: "100%",
              maxWidth: "660px",
              boxShadow:
                "0 1px 2px 1px hsla(0,0%,0%,0.08), 0 1px 3px 0 hsla(0,0%,0%,0.12), 0 2px 8px hsla(0,0%,0%,0.08)",
              border: "1px solid hsl(0,0%,77%)",
              overflow: "hidden",
            }}
          >
            {/* CKEditor-style header */}
            <div
              style={{
                background: "hsl(0,0%,96%)",
                borderBottom: "1px solid hsl(0,0%,77%)",
                padding: "10px 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <svg
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  style={{ fill: "hsl(0,0%,30%)" }}
                >
                  <path d="M6.91 3.96 2.05 10l4.86 6.04.92-.74L3.06 10l4.77-5.3-.92-.74zm6.18 0-.92.74L16.94 10l-4.77 5.3.92.74L18.95 10l-4.86-6.04z" />
                </svg>
                <span
                  style={{
                    fontWeight: "700",
                    fontSize: "13px",
                    color: "hsl(0,0%,20%)",
                    letterSpacing: "0.01em",
                  }}
                >
                  Insert HTML Block
                </span>
              </div>
              <button
                onClick={() => {
                  setShowHtmlModal(false);
                  setRawHtmlInput("");
                }}
                title="Close"
                style={{
                  background: "transparent",
                  border: "1px solid transparent",
                  borderRadius: "2px",
                  color: "hsl(0,0%,40%)",
                  fontSize: "18px",
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: "2px 6px",
                  display: "flex",
                  alignItems: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "hsl(0,0%,90%)";
                  e.currentTarget.style.borderColor = "hsl(0,0%,77%)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = "transparent";
                }}
              >
                &#xd7;
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px 18px" }}>
              <p
                style={{
                  fontSize: "12px",
                  color: "hsl(0,0%,40%)",
                  margin: "0 0 10px 0",
                  lineHeight: "1.5",
                }}
              >
                Paste your custom HTML below. It will be inserted at the current
                cursor position in the editor.
              </p>

              {/* CKEditor-style labeled input */}
              <div style={{ marginBottom: "12px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "hsl(0,0%,40%)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: "5px",
                  }}
                >
                  HTML Code
                </label>
                <textarea
                  autoFocus
                  value={rawHtmlInput}
                  onChange={(e) => setRawHtmlInput(e.target.value)}
                  placeholder={
                    '<div class="my-section">\n  <h2>Custom Heading</h2>\n  <p>Your content here...</p>\n</div>'
                  }
                  spellCheck={false}
                  style={{
                    width: "100%",
                    height: "200px",
                    fontFamily: '"Courier New",Courier,monospace',
                    fontSize: "12.5px",
                    border: "1px solid hsl(0,0%,78%)",
                    borderRadius: "2px",
                    padding: "8px 10px",
                    resize: "vertical",
                    outline: "none",
                    background: "hsl(0,0%,100%)",
                    color: "hsl(0,0%,15%)",
                    lineHeight: "1.7",
                    boxSizing: "border-box",
                    transition: "border-color 0.15s",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "hsl(208,79%,51%)";
                    e.target.style.boxShadow =
                      "0 0 0 2px hsla(208,79%,51%,0.2)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "hsl(0,0%,78%)";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Live preview */}
              {rawHtmlInput.trim() && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "hsl(0,0%,40%)",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      marginBottom: "5px",
                    }}
                  >
                    Preview
                  </label>
                  <div
                    dangerouslySetInnerHTML={{ __html: rawHtmlInput }}
                    style={{
                      border: "1px solid hsl(0,0%,82%)",
                      borderRadius: "2px",
                      padding: "10px 12px",
                      background: "hsl(0,0%,98%)",
                      fontSize: "13px",
                      maxHeight: "120px",
                      overflowY: "auto",
                      color: "hsl(0,0%,20%)",
                      lineHeight: "1.6",
                    }}
                  />
                </div>
              )}
            </div>

            {/* CKEditor-style footer */}
            <div
              style={{
                padding: "10px 18px",
                borderTop: "1px solid hsl(0,0%,88%)",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "8px",
                background: "hsl(0,0%,98%)",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setShowHtmlModal(false);
                  setRawHtmlInput("");
                }}
                style={{
                  padding: "5px 14px",
                  borderRadius: "2px",
                  border: "1px solid hsl(0,0%,77%)",
                  background: "hsl(0,0%,100%)",
                  color: "hsl(0,0%,20%)",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "hsl(0,0%,95%)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "hsl(0,0%,100%)")
                }
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={insertRawHtml}
                disabled={!rawHtmlInput.trim()}
                style={{
                  padding: "5px 16px",
                  borderRadius: "2px",
                  border:
                    "1px solid " +
                    (rawHtmlInput.trim()
                      ? "hsl(208,79%,42%)"
                      : "hsl(0,0%,77%)"),
                  background: rawHtmlInput.trim()
                    ? "hsl(208,79%,51%)"
                    : "hsl(0,0%,88%)",
                  color: rawHtmlInput.trim()
                    ? "hsl(0,0%,100%)"
                    : "hsl(0,0%,55%)",
                  fontWeight: "700",
                  fontSize: "13px",
                  cursor: rawHtmlInput.trim() ? "pointer" : "not-allowed",
                  fontFamily: "inherit",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (rawHtmlInput.trim())
                    e.currentTarget.style.background = "hsl(208,79%,44%)";
                }}
                onMouseLeave={(e) => {
                  if (rawHtmlInput.trim())
                    e.currentTarget.style.background = "hsl(208,79%,51%)";
                }}
              >
                Insert into Editor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CKEditor 5 for Blog Content */}

      <div className="my-3" style={{ marginTop: "0 !important" }}>
        <style>
          {`
            .ck-editor__editable {
              min-height: 400px;
            }
            
            /* Make CKEditor toolbar sticky */
            .ck.ck-editor__top {
              position: sticky !important;
              top: 65px !important;
              z-index: 1000 !important;
              background: white !important;
            }
            
            .ck.ck-sticky-panel .ck-toolbar {
              background: white !important;
              border-bottom: 1px solid var(--ck-color-toolbar-border);
            }
            
            /* Prevent CKEditor from adding extra space when focused */
            .ck.ck-editor__top.ck-reset_all {
              position: sticky !important;
              top: 65px !important;
            }
            
            .ck.ck-sticky-panel {
              position: static !important;
            }
            
            .ck.ck-sticky-panel__content {
              position: static !important;
            }
            
            /* Fix Bootstrap table class conflict - override .table background */
            figure.table table {
              background-color: transparent !important;
            }
            
            figure.table {
              background-color: transparent !important;
            }
            
            /* Preserve Google Docs paragraph spacing in editor */
            .ck-editor__editable p[style*="margin"] {
              margin-top: 12pt !important;
              margin-bottom: 12pt !important;
            }
            
            /* Ensure inline styles are rendered in the editor */
            .ck-editor__editable p {
              margin-top: revert;
              margin-bottom: revert;
            }
          `}
        </style>
        <CKEditor
          editor={ClassicEditor}
          config={editorConfiguration}
          data={editorData}
          onReady={(editor) => {
            editorRef.current = editor;
            console.log("CKEditor is ready to use!", editor);

            // Apply inline styles to the editable area so they render visually
            const editableElement = editor.editing.view.document.getRoot();
            editor.editing.view.change((writer) => {
              writer.setStyle("white-space", "normal", editableElement);
            });
          }}
          onChange={(event, editor) => {
            const data = editor.getData();

            // Check for deleted images before updating state
            handleImageDelete(data);

            // Update editor data and previous data reference
            setEditorData(data);
            previousEditorData.current = data;

            // Trigger auto-save
            handleContentChange(data);
          }}
          onBlur={(event, editor) => {
            console.log("Blur.", editor);
          }}
          onFocus={(event, editor) => {
            console.log("Focus.", editor);
          }}
        />
      </div>

      {/* Commented out old TextEditor - keeping for reference */}
      {/* 
      <div className="my-3">
        <TextEditor
          blog_title={inputData.blog_title}
          blog_image={rawFile}
          contentEditableRef={contentEditableRef}
          createBlogFn={createBlogFn}
          params={params}
          localStorageKey={"admin_selected_blog"}
          isSourceView={isSourceView}
          setIsSourceView={setIsSourceView}
        />
      </div>
      */}
      <SuccessModal
        open={openSuccessModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      <FailureModal
        open={openFailureModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
    </div>
  );
}

export default BlogPage;
